import { FormSectionDto, DraftDataDto } from "@green0meter/api-client";

export interface MappedQuestion {
  questionCode: string;
  questionName: string;
  answer: object;
  isTable: boolean;
}

export interface MappedSection {
  sectionId: string;
  sectionName: string;
  questions: MappedQuestion[];
}

export interface MappedForm {
  formId: string;
  formName: string;
  sections: MappedSection[];
}

/**
 * Processes form draft data and sections to create mapped form data structure
 * with proper handling of both regular questions and table questions
 */
export const processFormData = (
  draftData: DraftDataDto[],
  sections: FormSectionDto[],
  formId: string,
  formName: string,
): MappedForm | null => {
  const mappedForm: MappedForm = {
    formId,
    formName,
    sections: [],
  };

  const draftDataBySection = new Map<string, DraftDataDto[]>();

  sections.forEach((section) => {
    const regularQuestions = draftData.filter(
      (draft) =>
        !draft.parentQuestionCode &&
        section.formQuestions?.some((q) => q.code === draft.questionCode),
    );

    const tableQuestions = draftData.filter(
      (draft) =>
        draft.parentQuestionCode &&
        section.formQuestions?.some((q) => q.code === draft.parentQuestionCode),
    );

    const allSectionDraftData = [...regularQuestions, ...tableQuestions];

    if (allSectionDraftData.length > 0) {
      draftDataBySection.set(section.id, allSectionDraftData);
    }
  });

  draftDataBySection.forEach((sectionDraftData, sectionId) => {
    const section = sections.find((s) => s.id === sectionId);

    if (!section) return;

    const mappedSection: MappedSection = {
      sectionId: section.id,
      sectionName: section.name,
      questions: [],
    };

    const regularDrafts = sectionDraftData.filter(
      (draft) => !draft.parentQuestionCode,
    );

    regularDrafts.forEach((draft) => {
      const question = section.formQuestions?.find(
        (q) => q.code === draft.questionCode,
      );

      if (question) {
        mappedSection.questions.push({
          questionCode: draft.questionCode,
          questionName: question.name,
          answer: draft.value,
          isTable: false,
        });
      }
    });

    const tableDrafts = sectionDraftData.filter((draft) => draft.parentQuestionCode);
    const tableGroups = new Map<string, DraftDataDto[]>();

    tableDrafts.forEach((draft) => {
      const parentCode = draft.parentQuestionCode!;

      if (!tableGroups.has(parentCode)) {
        tableGroups.set(parentCode, []);
      }
      tableGroups.get(parentCode)!.push(draft);
    });

    tableGroups.forEach((tableRows, parentQuestionCode) => {
      const parentQuestion = section.formQuestions?.find(
        (q) => q.code === parentQuestionCode,
      );

      if (parentQuestion) {
        const rowsMap = new Map<number, Map<string, object>>();

        tableRows.forEach((draft) => {
          const rowIndex = draft.rowIndex || 1;

          if (!rowsMap.has(rowIndex)) {
            rowsMap.set(rowIndex, new Map());
          }
          rowsMap.get(rowIndex)!.set(draft.questionCode, draft.value);
        });

        // Convert to 2D array - organize columns consistently
        const tableData: (string | object | undefined)[][] = [];
        const sortedRowIndices = Array.from(rowsMap.keys()).sort((a, b) => a - b);

        const questionCodes = Array.from(
          new Set(tableRows.map((draft) => draft.questionCode)),
        );

        const headerRow: string[] = [];

        questionCodes.forEach((questionCode) => {
          const question = parentQuestion.childQuestions?.find(
            (q) => q.code === questionCode,
          );

          headerRow.push(question?.name || questionCode);
        });

        tableData.push(headerRow);

        sortedRowIndices.forEach((rowIndex) => {
          const rowData = rowsMap.get(rowIndex)!;
          const row: (string | object | undefined)[] = [];

          questionCodes.forEach((questionCode) => {
            row.push(rowData.get(questionCode));
          });

          tableData.push(row);
        });

        mappedSection.questions.push({
          questionCode: parentQuestionCode,
          questionName: parentQuestion.name,
          answer: tableData,
          isTable: true,
        });
      }
    });

    if (mappedSection.questions.length > 0) {
      mappedForm.sections.push(mappedSection);
    }
  });

  return mappedForm.sections.length > 0 ? mappedForm : null;
};
